import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiChevronRight, FiChevronDown, FiPlus, FiEye, FiEdit, FiTrash2, FiUser } from 'react-icons/fi';

export const SortableTreeNode = ({ 
  node, 
  expandedNodes, 
  toggleNode, 
  onView,
  viewingNodeId,
  onAdd, 
  onDelete, 
  onEdit,
  editingNodeId,
  onEditSubmit,
  isRoot = false 
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editData, setEditData] = useState({
    fullName: node.fullName,
    sponsorCode: node.sponsorCode,
    rank: node.rank,
    city: node.city,
    regDate: node.regDate,
    actDate: node.actDate
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: node._id,
    disabled: isRoot
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const isExpanded = expandedNodes[node._id] || false;
  const hasChildren = node.children && node.children.length > 0;

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Node View/Edit Mode */}
      {editingNodeId === node._id ? (
        <div className="bg-white dark:bg-gray-700 p-3 rounded-lg mb-2 border border-gray-200 dark:border-gray-600">
          <div className="space-y-2">
            <input
              value={editData.fullName}
              onChange={(e) => handleEditChange('fullName', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
              placeholder="Full Name"
            />
            <input
              value={editData.sponsorCode}
              onChange={(e) => handleEditChange('sponsorCode', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
              placeholder="Sponsor Code"
            />
            <select
              value={editData.rank}
              onChange={(e) => handleEditChange('rank', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
            >
              <option value="Associate">Associate (5%)</option>
              <option value="Sr. Associate">Sr. Associate (7%)</option>
              {/* Add all rank options */}
            </select>
            <input
              value={editData.city}
              onChange={(e) => handleEditChange('city', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
              placeholder="City"
            />
            <input
              value={editData.regDate}
              onChange={(e) => handleEditChange('regDate', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
              placeholder="Registration Date"
            />
            <input
              value={editData.actDate}
              onChange={(e) => handleEditChange('actDate', e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-600"
              placeholder="Activation Date"
            />
          </div>
          <div className="flex justify-end space-x-2 mt-3">
            <button 
              onClick={() => onEditSubmit(node._id, editData)}
              className="px-3 py-1 bg-blue-500 text-white rounded"
            >
              Save
            </button>
            <button 
              onClick={() => onEdit(null)}
              className="px-3 py-1 bg-gray-500 text-white rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div 
          className={`flex items-center p-3 rounded-lg mb-2 ${
            isRoot 
              ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-white' 
              : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
          } ${isDragging ? 'shadow-lg' : 'shadow-sm'}`}
        >
          {/* Toggle button */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(node._id)}
              className="mr-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              {isExpanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
            </button>
          )}
          
          {/* Node content */}
          <div className="flex-1 flex items-center" {...attributes} {...listeners}>
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white mr-3">
              <FiUser />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{node.fullName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{node.sponsorCode}</p>
              <div className="mt-1 flex items-center space-x-2">
                <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                  {node.rank}
                </span>
                {/* <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                  Earnings: ₹{node.totalEarnings?.toFixed(2) || '0.00'}
                </span> */}
              </div>
            </div>
          </div>

          {/* Actions */}
          {/* {!isRoot && ( */}
            <div className="flex space-x-2">
              <button
                onClick={() => onView(node._id)}
                className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-gray-600 rounded-full"
                title="View Details"
              >
                <FiEye size={16} />
              </button>
              {/* <button
                onClick={() => onAdd(node._id)}
                className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-gray-600 rounded-full"
                title="Add Member"
              >
                <FiPlus size={16} />
              </button> */}
              <button
                onClick={() => {
                  setEditData({
                    fullName: node.fullName,
                    sponsorCode: node.sponsorCode,
                    rank: node.rank
                  });
                  onEdit(node._id);
                }}
                className="p-1.5 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-gray-600 rounded-full"
                title="Edit Member"
              >
                <FiEdit size={16} />
              </button>
              <button
                onClick={() => confirmDelete ? onDelete(node._id) : setConfirmDelete(true)}
                className={`p-1.5 ${confirmDelete ? 'bg-red-500 text-white' : 'text-red-600 hover:bg-red-50'} rounded-full`}
                title={confirmDelete ? "Confirm Delete" : "Delete Member"}
              >
                {confirmDelete ? '✓' : <FiTrash2 size={16} />}
              </button>
              {confirmDelete && (
                <button 
                  onClick={() => setConfirmDelete(false)}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full ml-1"
                >
                  ×
                </button>
              )}
            </div>
        </div>
      )}

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="pl-6 border-l-2 border-gray-200 dark:border-gray-700 ml-4">
          {node.children.map((child) => (
            <SortableTreeNode
              key={child._id}
              node={child}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              onView={onView}
              viewingNodeId={viewingNodeId}
              onAdd={onAdd}
              onDelete={onDelete}
              onEdit={onEdit}
              editingNodeId={editingNodeId}
              onEditSubmit={onEditSubmit}
            />
          ))}
        </div>
      )}

      {/* View Modal */}
      {viewingNodeId === node._id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">{node.fullName}</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Sponsor Code:</span> {node.sponsorCode}</p>
              <p><span className="font-medium">Rank:</span> {node.rank}</p>
              <p><span className="font-medium">Email:</span> {node.email}</p>
              <p><span className="font-medium">Phone:</span> {node.phone}</p>
              <p><span className="font-medium">Join Date:</span> {new Date(node.createdAt).toLocaleDateString()}</p>
              <p><span className="font-medium">Total Earnings:</span> ₹{node.totalEarnings?.toFixed(2) || '0.00'}</p>
            </div>
            <button
              onClick={() => onView(null)}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};